/**
 * CustomFormsScreen — admin UI for Custom Forms.
 *
 * Tabbed editor (Build / Preview) sharing a single SchemaDraft.
 * Raw-JSON editing and the manual Rebuild Search Index action are kerkton-staff
 * tools and live in KST Portal, not here.
 * Design notes: docs/house-check-request/visual-builder-design.md
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  KstDataGrid,
  type KstDataGridColumn,
  type KstDataGridRows,
} from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';
import moment from 'moment';
import { AccessAuthorities } from '@/models';
import { getAvailableSubmodules, getOfficerPluralLabel } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
import { useFormSchemas } from '@/hooks/useFormSchemas';
import { ATTACH_TO_LABELS } from '@/models/form-schema';
import type {
  CreateFormSchemaCommandData,
  FormReindexImpactData,
  FormSchemaData,
  FormSchemaSaveResultData,
  FormSchemaValidationResultData,
  FormSchemaWarningData,
  FormSchemaDeleteResultData,
  UpdateFormSchemaCommandData,
} from '@/models/form-schema';

import { WysiwygSchemaEditor } from '@/components/forms/wysiwyg/WysiwygSchemaEditor';
import { WysiwygJsonTab, type WysiwygJsonTabHandle } from '@/components/forms/wysiwyg/WysiwygJsonTab';
import { useSchemaDraft } from '@/components/forms/builder/useSchemaDraft';
import {
  blankSchemaDraft,
  draftToSchemaCommand,
  schemaToDraft,
} from '@/components/forms/builder/schema-draft';
import type { FieldDraft, SchemaDraft } from '@/components/forms/builder/schema-draft';

interface ServerError {
  errorCode?: string;
  message?: string;
  field?: string;
}

function extractServerErrors(err: any): ServerError[] {
  const body = err?.responseJSON ?? err?.body ?? err;
  const embedded = body?.errors?._embedded?.errors;
  if (Array.isArray(embedded)) return embedded as ServerError[];
  if (Array.isArray(body?.errors)) return body.errors as ServerError[];
  return [];
}

function isCustomFormsReadOnly(): boolean {
  const submodules: string[] = getAvailableSubmodules() ?? [];
  return !isInArray(submodules, AccessAuthorities.ROLE_FORM_MANAGEMENT);
}

const schemaColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  {
    field: 'name',
    name: 'Name',
    size: 'col-xs-3',
    template: (e: any) => (hasValue(e) && hasValue(e.name) ? e.name : ''),
  },
  {
    field: 'attachTo',
    name: 'Used For',
    size: 'col-xs-2',
    template: (e: any) => {
      if (!hasValue(e) || !hasValue(e.attachTo)) return '';
      return ATTACH_TO_LABELS[String(e.attachTo)] ?? String(e.attachTo);
    },
  },
  {
    field: 'currentVersionNumber',
    name: 'Version',
    size: 'col-xs-1',
    template: (e: any) =>
      hasValue(e) && hasValue(e.currentVersionNumber) ? String(e.currentVersionNumber) : '',
  },
  {
    field: 'isActive',
    name: 'Active',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && e.isActive ? 'Yes' : 'No'),
  },
  {
    field: 'updatedAt',
    name: 'Last Updated',
    size: 'col-xs-3',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.updatedAt)) {
        return moment(e.updatedAt).format('MM/DD/YYYY HH:mm');
      }
      return '';
    },
  },
];

type EditorTab = 'design' | 'json';

export function CustomFormsScreen() {
  const { theme } = useTheme();
  const isReadOnly = useMemo(() => isCustomFormsReadOnly(), []);
  const officersLabel = useMemo(() => getOfficerPluralLabel().toLowerCase(), []);

  // null attachTo lists every host type, so client and case report forms are
  // managed from the same grid.
  const { schemas, isLoading, error: loadError, reload } = useFormSchemas({
    attachTo: null,
    autoLoad: true,
  });

  // Editor visibility + identity
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editorSchemaName, setEditorSchemaName] = useState<string>('');
  const [editorTab, setEditorTab] = useState<EditorTab>('design');

  // Single source of truth for the schema being edited.
  const draft = useSchemaDraft(blankSchemaDraft());

  // Advanced (raw JSON) tab integration. The textarea owns its own local edit
  // state separate from the draft until the user clicks Apply. We track its
  // dirtiness here so Save Changes can warn (instead of silently dropping
  // unapplied JSON edits) and offer to apply on the user's behalf.
  const jsonTabRef = useRef<WysiwygJsonTabHandle>(null);
  const [jsonDirty, setJsonDirty] = useState(false);
  const [confirmJsonUnappliedOpen, setConfirmJsonUnappliedOpen] = useState(false);

  // Switching away from the JSON tab unmounts WysiwygJsonTab, which loses its
  // local textarea state — any "unapplied edits" are gone with it. Clear the
  // parent-side dirty flag so the Save warning doesn't fire on a phantom dirty
  // state and so applyNow() (now ref-less) isn't reachable.
  useEffect(() => {
    if (editorTab !== 'json') setJsonDirty(false);
  }, [editorTab]);

  // Server feedback
  const [serverErrors, setServerErrors] = useState<ServerError[]>([]);
  const [warnings, setWarnings] = useState<FormSchemaWarningData[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Delete confirm
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FormSchemaData | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Reindex confirmation — shown before either a save or a per-field searchable toggle
  // when the change will trigger a sidecar rebuild. Severity is derived from
  // impact.affectedInstanceCount:
  //   < 50         silent (no modal)
  //   50–4,999     soft notice (info tone, default-action confirm)
  //   5,000–49,999 hard confirm (warning tone, requires explicit click)
  //   ≥ 50,000     blocked (no confirm action; user must close and contact admin)
  //
  // The modal shares state across two pending actions — either a save (pendingSaveCmd)
  // or a per-field toggle (pendingToggle). Whichever is set when the user clicks Confirm
  // is the action that runs.
  const [reindexConfirmOpen, setReindexConfirmOpen] = useState(false);
  const [reindexImpact, setReindexImpact] = useState<FormReindexImpactData | null>(null);
  const [pendingSaveCmd, setPendingSaveCmd] = useState<UpdateFormSchemaCommandData | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{
    schemaId: number;
    fieldKey: string;
    searchable: boolean;
  } | null>(null);

  useEffect(() => {
    if (loadError) {
      App.showPopupNotification(loadError, 'error');
    }
  }, [loadError]);

  const schemaRows: KstDataGridRows = useMemo(
    () => ({ entries: schemas as any[] }),
    [schemas],
  );

  // ── Editor open / close ────────────────────────────────────────────
  const resetEditorFeedback = useCallback(() => {
    setServerErrors([]);
    setWarnings([]);
  }, []);

  const openCreateEditor = useCallback(() => {
    if (isReadOnly) return;
    setEditorMode('create');
    setEditorSchemaName('');
    const fresh = blankSchemaDraft();
    draft.setDraft(fresh);
    setEditorTab('design');
    resetEditorFeedback();
    setEditorOpen(true);
  }, [isReadOnly, draft, resetEditorFeedback]);

  const openEditEditor = useCallback(
    (schema: FormSchemaData) => {
      resetEditorFeedback();
      setEditorMode('edit');
      setEditorSchemaName(schema.name);
      // Seed with whatever the list endpoint gave us; replace once detail loads.
      const seed = schemaToDraft(schema);
      draft.setDraft(seed);
      setEditorTab('design');
      setEditorOpen(true);

      setIsLoadingDetail(true);
      api.getFormSchema(
        schema.id,
        null,
        (result: FormSchemaData) => {
          setIsLoadingDetail(false);
          if (result) {
            setEditorSchemaName(result.name);
            const detailed = schemaToDraft(result);
            draft.setDraft(detailed);
          }
        },
        (err: any) => {
          setIsLoadingDetail(false);
          App.showPopupNotification(
            err?.message ?? 'Failed to load schema detail.',
            'error',
          );
        },
      );
    },
    [draft, resetEditorFeedback],
  );

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    resetEditorFeedback();
  }, [resetEditorFeedback]);

  // ── Save ───────────────────────────────────────────────────────────
  // Threshold below which a sidecar reindex is fast enough to not warrant a warning.
  // Above this, the save flow inserts a preview-update step to surface impact.
  const REINDEX_NOTICE_FLOOR = 50;
  const REINDEX_HARD_CONFIRM_FLOOR = 5000;

  /** Submit the actual create/update once we've decided to proceed. */
  const runActualSave = useCallback(
    (
      cmd: CreateFormSchemaCommandData | UpdateFormSchemaCommandData,
      mode: 'create' | 'edit',
      schemaId: number | null,
    ) => {
      const onSuccess = (result: FormSchemaSaveResultData) => {
        setIsSaving(false);
        setWarnings(result?.warnings ?? []);
        App.showPopupNotification('Form saved.', 'success');
        reload();
        if (result?.detail) {
          setEditorMode('edit');
          setEditorSchemaName(result.detail.name);
          const fromServer = schemaToDraft(result.detail);
          draft.setDraft(fromServer);
        }
      };
      const onError = (err: any) => {
        setIsSaving(false);
        const structured = extractServerErrors(err);
        if (structured.length > 0) setServerErrors(structured);
        else App.showPopupNotification(err?.message ?? 'Failed to save form.', 'error');
      };

      setIsSaving(true);
      if (mode === 'create') {
        api.createFormSchema(cmd as CreateFormSchemaCommandData, onSuccess, onError);
      } else if (schemaId != null) {
        api.updateFormSchema(schemaId, cmd as UpdateFormSchemaCommandData, onSuccess, onError);
      } else {
        setIsSaving(false);
      }
    },
    [draft, reload],
  );

  /**
   * Run the save flow against a specific draft snapshot. We accept an explicit
   * draft (rather than always reading from `draft.draft`) so the apply-then-save
   * path can pass the just-parsed JSON synchronously — React state updates from
   * `draft.setDraft(next)` won't be visible until the next render, so a closure
   * over `draft.draft` would still see the old sections at the moment of save.
   */
  const executeSaveWithDraft = useCallback((workingDraft: SchemaDraft) => {
    if (isReadOnly) return;
    resetEditorFeedback();

    const cmd: CreateFormSchemaCommandData | UpdateFormSchemaCommandData = draftToSchemaCommand(workingDraft);

    // Create flow has nothing to reindex (no instances yet) — straight to save.
    if (editorMode === 'create' || workingDraft.id == null) {
      runActualSave(cmd, editorMode, workingDraft.id);
      return;
    }

    // Update flow: preview first to surface reindex impact (and any blocking errors).
    const schemaId = workingDraft.id;
    setIsSaving(true);
    api.previewUpdateFormSchema(
      schemaId,
      cmd as UpdateFormSchemaCommandData,
      (validation: FormSchemaValidationResultData) => {
        setIsSaving(false);

        // Server-side errors override the impact dialog — surface them and bail.
        if (validation?.errors?.length) {
          setServerErrors(validation.errors.map((message) => ({ message })));
          return;
        }

        const impact = validation?.reindexImpact ?? null;
        const count = impact?.affectedInstanceCount ?? 0;

        // Cap exceeded — show blocked modal (no confirm action, user must close).
        if (impact?.capExceeded) {
          setReindexImpact(impact);
          setPendingSaveCmd(null); // null = blocked, no save to run on confirm
          setReindexConfirmOpen(true);
          return;
        }

        // No reindex, or below the notice floor — proceed silently.
        if (!impact?.willReindex || count < REINDEX_NOTICE_FLOOR) {
          runActualSave(cmd, 'edit', schemaId);
          return;
        }

        // Notice or hard-confirm tier — open modal, defer the save.
        setReindexImpact(impact);
        setPendingSaveCmd(cmd as UpdateFormSchemaCommandData);
        setReindexConfirmOpen(true);
      },
      (err: any) => {
        setIsSaving(false);
        const structured = extractServerErrors(err);
        if (structured.length > 0) setServerErrors(structured);
        else App.showPopupNotification(err?.message ?? 'Failed to validate form.', 'error');
      },
    );
  }, [
    isReadOnly,
    editorMode,
    resetEditorFeedback,
    runActualSave,
  ]);

  /**
   * Save Changes click handler. If the Advanced (JSON) textarea has edits the
   * user hasn't applied, intercept and ask — saving the draft as-is would
   * silently drop their typed JSON. The "Apply and Save" path runs Apply for
   * them and chains the save with the freshly-parsed draft.
   */
  const handleSave = useCallback(() => {
    if (isReadOnly) return;
    if (jsonDirty) {
      setConfirmJsonUnappliedOpen(true);
      return;
    }
    executeSaveWithDraft(draft.draft);
  }, [isReadOnly, jsonDirty, draft, executeSaveWithDraft]);

  const handleConfirmApplyAndSave = useCallback(() => {
    setConfirmJsonUnappliedOpen(false);
    const next = jsonTabRef.current?.applyNow();
    if (next == null) {
      // Parse failed — JSON tab surfaces the error inline. Don't proceed.
      return;
    }
    executeSaveWithDraft(next);
  }, [executeSaveWithDraft]);

  /** Apply a per-field searchable toggle. Called from the confirm-reindex flow OR
   *  directly when the impact is below the notice floor. */
  const runToggleSearchable = useCallback(
    (schemaId: number, fieldKey: string, searchable: boolean) => {
      setIsSaving(true);
      api.toggleSearchable(
        schemaId,
        fieldKey,
        { searchable },
        (result: FormSchemaSaveResultData) => {
          setIsSaving(false);
          setWarnings(result?.warnings ?? []);
          App.showPopupNotification(
            `'${fieldKey}' searchable: ${searchable ? 'on' : 'off'}.`,
            'success',
          );
          reload();
          if (result?.detail) {
            setEditorMode('edit');
            setEditorSchemaName(result.detail.name);
            const fromServer = schemaToDraft(result.detail);
            draft.setDraft(fromServer);
          }
        },
        (err: any) => {
          setIsSaving(false);
          const structured = extractServerErrors(err);
          if (structured.length > 0) setServerErrors(structured);
          else App.showPopupNotification(err?.message ?? 'Failed to update searchable.', 'error');
        },
      );
    },
    [draft, reload],
  );

  /** Entry point from the schema editor's per-field "Manage" button. Calls preview
   *  first to surface impact; routes through the confirm modal for non-trivial costs
   *  or applies the toggle directly for cheap cases. */
  const handleRequestSearchableToggle = useCallback(
    (field: FieldDraft) => {
      if (isReadOnly) return;
      const schemaId = draft.draft.id;
      if (schemaId == null || !field.key) return;
      const newSearchable = !field.searchable;

      setIsSaving(true);
      api.previewSearchableToggle(
        schemaId,
        field.key,
        { searchable: newSearchable },
        (validation: FormSchemaValidationResultData) => {
          setIsSaving(false);
          if (validation?.errors?.length) {
            setServerErrors(validation.errors.map((message) => ({ message })));
            return;
          }
          const impact = validation?.reindexImpact ?? null;
          const count = impact?.affectedInstanceCount ?? 0;

          if (impact?.capExceeded) {
            setReindexImpact(impact);
            setPendingToggle(null); // null = blocked, no commit on confirm
            setPendingSaveCmd(null);
            setReindexConfirmOpen(true);
            return;
          }
          if (!impact?.willReindex || count < REINDEX_NOTICE_FLOOR) {
            // Cheap (e.g., requested value already matches, or no live instances) —
            // apply directly without the modal.
            runToggleSearchable(schemaId, field.key, newSearchable);
            return;
          }
          setReindexImpact(impact);
          setPendingToggle({ schemaId, fieldKey: field.key, searchable: newSearchable });
          setPendingSaveCmd(null);
          setReindexConfirmOpen(true);
        },
        (err: any) => {
          setIsSaving(false);
          const structured = extractServerErrors(err);
          if (structured.length > 0) setServerErrors(structured);
          else App.showPopupNotification(err?.message ?? 'Failed to preview searchable change.', 'error');
        },
      );
    },
    [isReadOnly, draft, runToggleSearchable],
  );

  const handleConfirmReindex = useCallback(() => {
    // Branch on which pending action is set. Toggle takes precedence if both somehow set.
    if (pendingToggle) {
      const { schemaId, fieldKey, searchable } = pendingToggle;
      setReindexConfirmOpen(false);
      setReindexImpact(null);
      setPendingToggle(null);
      setPendingSaveCmd(null);
      runToggleSearchable(schemaId, fieldKey, searchable);
      return;
    }
    const cmd = pendingSaveCmd;
    const schemaId = draft.draft.id;
    setReindexConfirmOpen(false);
    setReindexImpact(null);
    setPendingSaveCmd(null);
    setPendingToggle(null);
    if (cmd && schemaId != null) {
      runActualSave(cmd, 'edit', schemaId);
    }
  }, [pendingSaveCmd, pendingToggle, draft.draft.id, runActualSave, runToggleSearchable]);

  const handleCancelReindex = useCallback(() => {
    setReindexConfirmOpen(false);
    setReindexImpact(null);
    setPendingSaveCmd(null);
    setPendingToggle(null);
  }, []);

  // ── Delete ─────────────────────────────────────────────────────────
  const requestDelete = useCallback(
    (schema: FormSchemaData) => {
      if (isReadOnly) return;
      api.previewDeleteFormSchema(
        schema.id,
        (result: FormSchemaDeleteResultData) => {
          const count = result?.affectedInstanceCount ?? 0;
          setDeleteTarget(schema);
          setDeleteMessage(
            `${count} submission${count === 1 ? '' : 's'} will be kept for historical reference. Delete this form?`,
          );
          setDeleteConfirmOpen(true);
        },
        (err: any) => {
          App.showPopupNotification(err?.message ?? 'Failed to preview deletion.', 'error');
        },
      );
    },
    [isReadOnly],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    api.deleteFormSchema(
      deleteTarget.id,
      () => {
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setDeleteTarget(null);
        setDeleteMessage('');
        App.showPopupNotification('Form deleted.', 'success');
        if (draft.draft.id === deleteTarget.id) {
          setEditorOpen(false);
        }
        reload();
      },
      (err: any) => {
        setIsDeleting(false);
        App.showPopupNotification(err?.message ?? 'Failed to delete form.', 'error');
      },
    );
  }, [deleteTarget, draft.draft.id, reload]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    setDeleteMessage('');
  }, []);

  // ── Header buttons + selection actions ─────────────────────────────
  const headerButtons = useMemo(() => {
    if (isReadOnly) {
      return [{ name: 'View', function: 'edit', src: '/Content/TourImages/pen.svg' }];
    }
    return [
      { name: 'New', function: 'add', src: '/Content/TourImages/add-r.svg' },
      { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
      { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
    ];
  }, [isReadOnly]);

  const handleButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'add') {
        openCreateEditor();
        return;
      }
      if (functionName === 'edit') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Select a form to edit.', 'warning');
          return;
        }
        if (checkedItems.length > 1) {
          App.showPopupNotification('Edit one form at a time.', 'warning');
          return;
        }
        openEditEditor(checkedItems[0] as FormSchemaData);
        return;
      }
      if (functionName === 'delete') {
        if (isReadOnly) return;
        if (checkedItems.length === 0) {
          App.showPopupNotification('Select a form to delete.', 'warning');
          return;
        }
        if (checkedItems.length > 1) {
          App.showPopupNotification(
            'Delete one form at a time.',
            'warning',
          );
          return;
        }
        requestDelete(checkedItems[0] as FormSchemaData);
      }
    },
    [isReadOnly, openCreateEditor, openEditEditor, requestDelete],
  );

  const gridColumns = useMemo<KstDataGridColumn[]>(() => schemaColumns, []);

  // ── Reindex confirm modal content ─────────────────────────────────
  // Build modal copy from the current impact state. severity is derived once at render time.
  // Bucket the duration into approximate phrases — the server's estimatedSeconds is a heuristic,
  // not a literal SLA.
  const reindexCopy = useMemo(() => {
    if (!reindexImpact) {
      return null;
    }
    const count = reindexImpact.affectedInstanceCount;
    const cap = reindexImpact.cap;
    const seconds = reindexImpact.estimatedSeconds;
    const plural = count === 1 ? '' : 's';
    let durationPhrase = 'a few seconds';
    if (seconds >= 60) durationPhrase = 'over a minute';
    else if (seconds >= 30) durationPhrase = 'about a minute';
    else if (seconds >= 15) durationPhrase = `about ${seconds} seconds`;
    else if (seconds >= 5) durationPhrase = `about ${seconds} seconds`;

    if (reindexImpact.capExceeded) {
      return {
        title: 'Cannot apply this change',
        message: `${count.toLocaleString()} submission${plural} exceeds the editor limit of ${cap.toLocaleString()}. Contact your Kerkton support representative.`,
        canConfirm: false,
        confirmText: '',
        cancelText: 'Close',
        confirmClass: 'danger',
      };
    }
    if (count >= REINDEX_HARD_CONFIRM_FLOOR) {
      return {
        title: 'This change rebuilds the search index',
        message: `Rebuilds the search index for ${count.toLocaleString()} filled-out form${plural} (about ${durationPhrase}). Other admins editing this form will queue. Continue?`,
        canConfirm: true,
        confirmText: 'Continue',
        cancelText: 'Cancel',
        confirmClass: 'danger',
      };
    }
    // Soft notice tier (50 ≤ N < 5,000)
    return {
      title: 'Search index will rebuild',
      message: `Updates the search index for ${count.toLocaleString()} filled-out form${plural} (about ${durationPhrase}).`,
      canConfirm: true,
      confirmText: 'Continue',
      cancelText: 'Cancel',
      confirmClass: 'primary',
    };
  }, [reindexImpact]);

  return (
    <div className={theme}>
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Form"
        message={deleteMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ConfirmModal
        open={reindexConfirmOpen && reindexCopy != null}
        title={reindexCopy?.title ?? ''}
        message={reindexCopy?.message ?? ''}
        confirmText={reindexCopy?.confirmText ?? 'Save'}
        cancelText={reindexCopy?.cancelText ?? 'Cancel'}
        confirmClass={reindexCopy?.confirmClass ?? 'primary'}
        confirmDisabled={isSaving}
        busy={isSaving}
        showCancel={true}
        theme={theme}
        autoFocus={reindexCopy?.canConfirm ? 'cancel' : 'cancel'}
        onConfirm={reindexCopy?.canConfirm ? handleConfirmReindex : undefined}
        onCancel={handleCancelReindex}
      />

      <ConfirmModal
        open={confirmJsonUnappliedOpen}
        title="Apply your changes first?"
        message="You have changes in the Advanced tab that haven't been added to the form. Click Apply and save to add them now."
        confirmText="Apply and save"
        cancelText="Cancel"
        confirmClass="primary"
        theme={theme}
        onConfirm={handleConfirmApplyAndSave}
        onCancel={() => setConfirmJsonUnappliedOpen(false)}
      />

      <div className="kst-page-container">
        <div className="kst-layout">
          <div className="kst-main-content">
            <div className="kst-page">
              {!editorOpen && (
                <>
                  <div className="kst-page-header">
                    <h3 className="kst-page-title">Custom Forms</h3>
                    <p className="kst-page-description">
                      Build the forms your {officersLabel} fill out on client records and case reports. House checks, equipment audits, supplemental report sections, anything you need.
                      {isReadOnly ? ' You can view forms here but don\'t have permission to edit them.' : ''}
                    </p>
                  </div>

                  <KstDataGrid
                    columns={gridColumns}
                    rows={schemaRows}
                    headerbuttons={headerButtons}
                    showcheckbox
                    mobiletitlefield="name"
                    loading={isLoading}
                    onButtonCommand={handleButtonCommand}
                  />
                </>
              )}

              {editorOpen && (
                <div className={`kst-form-builder ${theme ?? ''}`}>
                  <div className="kst-fb-back-row">
                    <button
                      type="button"
                      className="kst-fb-back-btn"
                      onClick={closeEditor}
                      disabled={isSaving}
                    >
                      ← Back to forms
                    </button>
                  </div>
                  <div className="kst-fb-page-header">
                    <div className="kst-fb-page-header-text">
                      <h3 className="kst-page-title">
                        {editorMode === 'create'
                          ? 'New Form'
                          : `Edit Form: ${editorSchemaName || `#${draft.draft.id ?? ''}`}`}
                      </h3>
                      <p className="kst-page-description">
                        {isLoadingDetail
                          ? 'Loading…'
                          : `Click any field or section to open the editor. The form below shows exactly what your ${officersLabel} will see.`}
                      </p>
                    </div>
                    {/* Action row up here (instead of bottom of editor) so admins
                        editing a long schema don't have to scroll past every section
                        to save. The buttons are persistent across both editor tabs. */}
                    <div className="kst-fb-button-row kst-fb-button-row--top">
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--primary"
                        onClick={handleSave}
                        disabled={isReadOnly || isSaving || isLoadingDetail}
                      >
                        {isSaving ? 'Saving…' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--secondary"
                        onClick={closeEditor}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      {editorMode === 'edit' && draft.draft.id != null && (
                        <button
                          type="button"
                          className="kstc-btn kstc-btn--danger"
                          onClick={() => {
                            const current = schemas.find((s) => s.id === draft.draft.id);
                            if (current) requestDelete(current);
                          }}
                          disabled={isReadOnly || isDeleting || isSaving || isLoadingDetail}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div role="tablist" className="wysiwyg-editor-tabs">
                    {(['design', 'json'] as EditorTab[]).map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="tab"
                        aria-selected={editorTab === t}
                        className={`wysiwyg-editor-tab${editorTab === t ? ' wysiwyg-editor-tab--active' : ''}`}
                        onClick={() => setEditorTab(t)}
                      >
                        {t === 'design' ? 'Design' : 'Advanced'}
                      </button>
                    ))}
                  </div>

                  {serverErrors.length > 0 && (
                    <div className="kst-error-panel" role="alert">
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Errors:</div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {serverErrors.map((se, i) => (
                          <li key={`err-${i}`} data-error-code={se.errorCode}>
                            {se.message ?? se.errorCode ?? 'Unknown error'}
                            {se.field ? ` (field: ${se.field})` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {warnings.length > 0 && (
                    <div className="kst-warnings-panel">
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Warnings:</div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {warnings.map((w, i) => (
                          <li key={`warn-${i}`} data-warning-code={w.code}>
                            {w.message}
                            {w.fieldKey ? ` (field: ${w.fieldKey})` : ''}
                            {w.affectedInstanceCount != null
                              ? ` (${w.affectedInstanceCount} filled-out form${w.affectedInstanceCount === 1 ? '' : 's'} affected)`
                              : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isLoadingDetail ? (
                    // Show a spinner while the detail load is in flight. Without this gate,
                    // the editor renders against the list-endpoint seed (name + attachTo only,
                    // no sections) which looks indistinguishable from a brand-new form.
                    <div className="loading-state" role="status" aria-live="polite">
                      <i className="fas fa-spinner fa-spin"></i> Loading form…
                    </div>
                  ) : (
                    <>
                      {editorTab === 'design' && (
                        <WysiwygSchemaEditor
                          draft={draft}
                          onRequestSearchableToggle={handleRequestSearchableToggle}
                          disabled={isSaving}
                        />
                      )}

                      {editorTab === 'json' && (
                        <WysiwygJsonTab
                          ref={jsonTabRef}
                          draft={draft}
                          disabled={isSaving}
                          onDirtyChange={setJsonDirty}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomFormsScreen;
